import { type Option, RowKey } from "@tento-lona/sheets";
import { type RowSection } from "@tento-lona/sheets/platform";
import { RowLocalId } from "@tento-lona/sheets";
import { SheetLayoutSpec } from "@tento-core/sheet-layout/sheet-layout-spec";
import { NaiveDate } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { LiveData } from "@tento-core/livedata";
import { Point } from "@tento-core/point";
import { NumRangeLike } from "@tento-core/range";
import { Rational } from "@tento-core/rational";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { rowIdentity } from "@tento-ui/components/sheet/row-identity";
import type { Inset } from "@tento-core/inset";
import type { SheetDelegate } from "./sheet-delegate.ts";
import type { SheetBinder } from "./sheet-index-renderer.ts";
import type { SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import type { SheetViewModel } from "@tento-lona";

/**
 * The sheet element's fixed-child handle (F11): reveal / update / hide fixed
 * children (menus, editors, overlays anchored to a row) through the layout's
 * fixed-children surface. Moved out of sheet-element.ts unchanged; the element
 * hands in its current sheet, its layout's children and the body-offset rule
 * (`SheetElement.Layout.bodyFixedChildTop`), all read when a method runs.
 */

/** What the handle reads from its sheet element. */
export type FixedChildHandleHost = {
  /** The bound sheet, if any. */
  readonly sheet: () => Option<SheetViewModel.Surface<SheetBinder>>;
  /** The layout's children surfaces. */
  readonly children: () => SheetLayoutPort["children"];
  /** `SheetElement.Layout.bodyFixedChildTop`. */
  readonly bodyFixedChildTop: (offsetPx: number, location: string, sheet: SheetViewModel.Surface<SheetBinder>) => number;
};

/** The handle the element exposes as `fixedChildHandle`. */
export function makeFixedChildHandle(host: FixedChildHandleHost): SheetDelegate.FixedChildHandle {
  return {
    reveal: (
      identifier: string,
      $content: HTMLElement,
      position: Point<ColumnIndex.Key.Resolved.WithSubindex, RowLocalId>,
      options: Option<{
        animate?: boolean;
        inset?: Partial<Inset>;
        end?: ColumnIndex.Key.Resolved.WithSubindex;
        subindex?: Rational;
        widthBehavior?: "fill" | "fill-right" | "auto" | number;
      }>,
    ) => {
      const rid = position.y;
      // A virtual aggregate row (e.g. `virtual(<ownerHex>|calendar-body)`, which
      // the calendar context menu anchors to) has no layout of its own
      // (`getRowLocation(rid)` is null) — resolve the OWNING row encoded in the
      // virtual id, exactly as `popoverHandle.reveal` above does. Without this the
      // fixed child's `rowLayout.get(rid)` misses, `reveal` returns early, and the
      // menu is built but never mounted ("context menu won't open").
      const virtual = rid.key.asVirtual();
      const ridIsRendered = host.sheet()?.joinedTree.getRowLocation(rid) != null;
      const ownerId =
        virtual != null && !ridIsRendered && host.sheet() != null
          ? nonnull(host.sheet(), "Sheet: no sheet bound").ids.resolve(RowKey.user(virtual.parentHex))
          : rid;
      const rowEntry =
        host.sheet()?.layout.rowLayout.get(ownerId) ??
        host.sheet()?.layout.rowLayout.get(rid);
      if (!rowEntry) return;
      const location = host.sheet()?.joinedTree.getRowLocation(ownerId) ?? "body";
      // Body fixed children clear the header the same way `--body-margin-top`
      // does; footer offsets are pre-rebased to footer-relative by SheetLayoutSpec.make.
      const yPx = host.bodyFixedChildTop(
        rowEntry.offsetPx,
        location,
        nonnull(host.sheet(), "Sheet: no sheet bound"),
      );

      $content.setAttribute("sheet-id", nonnull(host.sheet(), "Sheet: no sheet bound").handle.network.id);
      $content.setAttribute("row-id", position.y.toString());

      host.children().fixed.reveal(
        identifier,
        $content,
        {
          x: position.x,
          y: new LiveData(yPx),
        },
        location,
        options ?? undefined,
      );
    },
    revealDates: (
      identifier: string,
      $content: HTMLElement,
      position: Point<NaiveDate, RowLocalId>,
      options: Option<{
        animate?: boolean;
        inset?: Partial<Inset>;
        endDate?: NaiveDate;
        widthBehavior?: "fill" | "fill-right" | "auto" | number;
      }>,
    ) => {
      const rid = position.y;
      const rowEntry = host.sheet()?.layout.rowLayout.get(rid);
      if (!rowEntry) return;
      const location = host.sheet()?.joinedTree.getRowLocation(rid) ?? "body";
      // Body fixed children clear the header the same way `--body-margin-top`
      // does; footer offsets are pre-rebased to footer-relative by SheetLayoutSpec.make.
      const yPx = host.bodyFixedChildTop(
        rowEntry.offsetPx,
        location,
        nonnull(host.sheet(), "Sheet: no sheet bound"),
      );
      const insetTop = options?.inset?.top ?? 0;
      const totalY = yPx + insetTop;

      $content.setAttribute("sheet-id", nonnull(host.sheet(), "Sheet: no sheet bound").handle.network.id);
      $content.setAttribute("row-id", position.y.toString());
      // Store inset for rebindFixedChildYPositions to use
      $content.setAttribute("data-inset-top", String(insetTop));

      host.children().fixed.revealDates(
        identifier,
        $content,
        {
          x: position.x,
          y: new LiveData(yPx),
        },
        location,
        options ?? undefined,
      );
    },
    revealLive: (
      identifier: string,
      $content: HTMLElement,
      position: { x: LiveData<number>; y: RowLocalId },
      location: RowSection,
    ) => {
      const rid = position.y;
      const rowEntry = host.sheet()?.layout.rowLayout.get(rid);
      if (!rowEntry) return;
      const loc = location ?? host.sheet()?.joinedTree.getRowLocation(rid) ?? "body";

      // Body fixed children clear the header the same way `--body-margin-top`
      // does; footer offsets are pre-rebased to footer-relative by SheetLayoutSpec.make.
      const yPx = host.bodyFixedChildTop(
        rowEntry.offsetPx,
        loc,
        nonnull(host.sheet(), "Sheet: no sheet bound"),
      );

      host.children().fixed.revealLive(
        identifier,
        $content,
        { x: position.x, y: new LiveData(yPx) },
        loc,
      );
    },
    dismiss: (identifier: string, motion: SheetDelegate.Motion) => {
      host.children().fixed.dismiss(identifier, motion === "animated");
    },
    hasFixedChild: (identifier: string) => {
      return host.children().fixed.hasFixedChild(identifier);
    },
    getFixedChild: (identifier: string) => {
      return host.children().fixed.getFixedChild(identifier) ?? null;
    },
    updateFixedChildInset: (
      identifier: string,
      newInset: Partial<Inset>,
      rowId: RowLocalId,
    ): boolean => {
      const sheet = host.sheet();
      if (!sheet) return false;
      const rowLayout = sheet.layout.rowLayout.get(rowId);
      if (!rowLayout) return false;
      const location = sheet.joinedTree.getRowLocation(rowId) ?? "body";
      // Resolve the scroll-relative row top exactly as reveal() does (via
      // Sheet.Layout.bodyFixedChildTop), so re-positioning an existing fixed
      // child on inset change lands on the same pixel reveal placed it — the
      // header's 1px bottom divider included. The handle no longer re-derives
      // a hardcoded offset.
      return host.children().fixed.updateFixedChildInset(
        identifier,
        newInset,
        host.bodyFixedChildTop(rowLayout.offsetPx, location, sheet),
      );
    },
    repositionFixedChild: (
      identifier: string,
      column: ColumnIndex.Key.Resolved.WithSubindex,
      insetLeftPx: number,
    ): boolean => {
      return host.children().fixed.repositionFixedChild(
        identifier,
        column,
        insetLeftPx,
      );
    },
    updateFixedChildWidth: (identifier: string, widthPx: number): boolean => {
      return host.children().fixed.updateFixedChildWidth(
        identifier,
        widthPx,
      );
    },
    setFixedChildrenForRow: (
      rowId: RowLocalId,
      children: SheetDelegate.FixedChildSpec[],
      options: {
        owner: string;
      },
    ): void => {
      const sheet = host.sheet();
      if (!sheet) return;
      const layoutRowId = rowIdentity(rowId.toString());
      const rowLayout = sheet.layout.rowLayout.get(rowId);
      if (!rowLayout) {
        // Row not in current layout — dismiss stale children
        host.children().fixed.setFixedChildrenForRow(
          layoutRowId,
          [],
          options,
          0,
          "body",
        );
        return;
      }
      const location = sheet.joinedTree.getRowLocation(rowId) ?? "body";
      // Footer row offsets are already rebased to footer-relative by SheetLayoutSpec.make
      const rowOffsetPx = rowLayout.offsetPx;

      host.children().fixed.setFixedChildrenForRow(
        layoutRowId,
        children,
        options,
        rowOffsetPx,
        location,
      );
    },
    clearFixedChildrenByOwner: (owner: string): void => {
      host.children().fixed.clearFixedChildrenByOwner(owner);
    },
    registerSpecs: (
      rowId: RowLocalId,
      owner: string,
      specs: SheetDelegate.FixedChildSpec[],
    ): void => {
      host.children().fixed.registerSpecs(
        rowIdentity(rowId.toString()),
        owner,
        specs,
      );
    },
    reconcileSpecs: (
      rowId: RowLocalId,
      owner: string,
      visibleRange: NumRangeLike,
    ): SheetDelegate.FixedChildSpec[] => {
      return host.children().fixed.reconcileSpecs(
        rowIdentity(rowId.toString()),
        owner,
        visibleRange,
      );
    },
    clearSpecs: (rowId: RowLocalId, owner: string): void => {
      host.children().fixed.clearSpecs(
        rowIdentity(rowId.toString()),
        owner,
      );
    },
    suppressSpec: (specId: string): void => {
      host.children().fixed.suppressSpec(specId);
    },
    specRegistryKeys: (): IterableIterator<string> => {
      return host.children().fixed.specRegistryKeys();
    },
  };
}
